import * as React from "react";

import { cn } from "@/lib/utils";

interface TitleElementProps {
  type: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const TitleElement = React.forwardRef<HTMLInputElement, TitleElementProps>(
  ({ type, value, onChange, onBlur, placeholder, ...props }, ref) => {
    // Local state to track input value for immediate UI feedback
    const [localValue, setLocalValue] = React.useState(value);

    // Update local value when prop value changes
    React.useEffect(() => {
      setLocalValue(value);
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // Prevent interaction when disabled
      if (props.disabled) return;
      
      // Only update local state for immediate UI feedback
      setLocalValue(e.target.value);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      // Prevent interaction when disabled
      if (props.disabled) return;
      
      const finalValue = e.target.value;
      
      // Only call onBlur for confirmation - don't call onChange
      // The parent components use onBlur for the actual updates
      if (onBlur) {
        onBlur(finalValue);
      }
    };

    // Ensure we have a string value for display
    const displayValue =
      typeof localValue === "string"
        ? localValue
        : localValue !== undefined && localValue !== null
        ? String(localValue)
        : "";

    return (
      <input
        type={type}
        className={cn(
          "flex h-10 w-full py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 text-foreground",
          props.disabled 
            ? "border-0 bg-transparent" 
            : "border border-input bg-background px-3"
        )}
        placeholder={placeholder}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        ref={ref}
        {...props}
      />
    );
  }
);
TitleElement.displayName = "TitleElement";

export { TitleElement };
